import {
  chartsTooltipClasses,
  ChartsTooltipContainer,
  type ChartsTooltipProps,
  useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';
import { s } from '@/theme/tokens';

type StackedTotalsTooltipProps = ChartsTooltipProps & {
  totalsByLabel: ReadonlyMap<string, number>;
  tooltipLabelByAxisLabel?: ReadonlyMap<string, string>;
  tooltipLabelsByIndex?: readonly string[];
  tooltipLabelFormatter?: (label: string) => string;
};

type SeriesItemWithDataIndex = {
  dataIndex?: number;
};

export const StackedTotalsTooltip = ({
  totalsByLabel,
  tooltipLabelByAxisLabel,
  tooltipLabelsByIndex,
  tooltipLabelFormatter,
  classes,
  sx,
  ...props
}: StackedTotalsTooltipProps): JSX.Element | null => {
  const tooltipData = useAxesTooltip();
  if (!tooltipData) return null;
  const hasAnyBreakdownRows = tooltipData.some(({ axisFormattedValue, seriesItems }) => {
    const derivedTotal = seriesItems.reduce((sum, item) => {
      const value = typeof item.value === 'number' ? item.value : Number(item.value);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    const total = derivedTotal > 0 ? derivedTotal : (totalsByLabel.get(axisFormattedValue) ?? 0);
    const hasVisibleSeries = seriesItems.some(({ formattedValue }) => Boolean(formattedValue));
    return total > 0 || hasVisibleSeries;
  });
  const paperClassName = `${classes?.paper ?? ''} ${
    hasAnyBreakdownRows ? '' : 'flowqa-stacked-tooltip-paper-empty'
  }`.trim();
  const tableClassName = `${chartsTooltipClasses.table} ${classes?.table ?? ''}`.trim();
  const rowClassName = chartsTooltipClasses.row;
  const baseCellClassName = `${chartsTooltipClasses.cell} ${classes?.cell ?? ''}`.trim();
  const labelCellClassName = `${baseCellClassName} ${chartsTooltipClasses.labelCell}`.trim();
  const valueCellClassName = `${baseCellClassName} ${chartsTooltipClasses.valueCell}`.trim();
  const markContainerClassName = chartsTooltipClasses.markContainer;
  const markClassName = `${chartsTooltipClasses.mark} ${classes?.mark ?? ''}`.trim();

  return (
    <ChartsTooltipContainer classes={classes} sx={sx} {...props}>
      <div className={paperClassName}>
        {tooltipData.map(({ axisId, axisValue, axisFormattedValue, seriesItems }) => {
          const hoveredDataIndex = (
            seriesItems as Array<(typeof seriesItems)[number] & SeriesItemWithDataIndex>
          ).find(
            (item) => typeof item.dataIndex === 'number' && Number.isFinite(item.dataIndex),
          )?.dataIndex;
          const tooltipAxisLabel =
            (typeof hoveredDataIndex === 'number'
              ? tooltipLabelsByIndex?.[hoveredDataIndex]
              : undefined) ??
            (typeof axisValue === 'string' ? tooltipLabelFormatter?.(axisValue) : undefined) ??
            tooltipLabelByAxisLabel?.get(axisFormattedValue) ??
            axisFormattedValue;
          const derivedTotal = seriesItems.reduce((sum, item) => {
            const value = typeof item.value === 'number' ? item.value : Number(item.value);
            return Number.isFinite(value) ? sum + value : sum;
          }, 0);
          const total =
            derivedTotal > 0 ? derivedTotal : (totalsByLabel.get(axisFormattedValue) ?? 0);
          const showTotal = total > 0;
          const hasVisibleBreakdownRows =
            showTotal || seriesItems.some(({ formattedValue }) => Boolean(formattedValue));
          return (
            <table
              key={String(axisId)}
              className={`${tableClassName} ${
                hasVisibleBreakdownRows ? '' : 'flowqa-stacked-tooltip-table-empty'
              }`.trim()}
            >
              <thead>
                <tr className="flowqa-stacked-tooltip-header-row">
                  <th className={labelCellClassName}>{tooltipAxisLabel}</th>
                  {showTotal ? (
                    <td
                      className={`${valueCellClassName} flowqa-stacked-tooltip-header-total-label`}
                      colSpan={2}
                    >
                      {`Total: ${total}`}
                    </td>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {seriesItems.map(({ seriesId, color, formattedLabel, formattedValue }) => {
                  if (!formattedValue) return null;
                  const numericValue = Number(formattedValue);
                  const percentage =
                    total > 0 && Number.isFinite(numericValue)
                      ? `(${Math.round((numericValue / total) * 100)}%)`
                      : null;
                  return (
                    <tr key={String(seriesId)} className={rowClassName}>
                      <th className={labelCellClassName}>
                        <div className={markContainerClassName}>
                          <span
                            className={markClassName}
                            style={{
                              display: 'inline-block',
                              width: s('lg'),
                              height: s('lg'),
                              borderRadius: 2,
                              backgroundColor: color,
                            }}
                          />
                        </div>
                        {formattedLabel ?? ''}
                      </th>
                      <td className={`${valueCellClassName} flowqa-stacked-tooltip-count-cell`}>
                        {formattedValue}
                      </td>
                      <td className={`${valueCellClassName} flowqa-stacked-tooltip-percent-cell`}>
                        {percentage}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })}
      </div>
    </ChartsTooltipContainer>
  );
};
